import tinymce from 'tinymce/tinymce';
import { tinymceDE } from './translations';
import { Setup, config } from './config';

/* Required default icons */
import 'tinymce/icons/default';

/* Required TinyMCE components */
import 'tinymce/themes/silver';
import 'tinymce/models/dom/';

/* Import plugins */
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/autosave';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/code';
import 'tinymce/plugins/media';

type ExtendedConfig = Partial<Setup> & {
  [key: string]: unknown;
};

type Widgets = {
  [key: string]: {
    id: string;
    config: Partial<Setup> | ExtendedConfig;
  };
};
/**
 * Initialize TinyMCE widget for a given selector with a given configuration.
 */
export const prepareEditor = (selector: string, configuration: object) => {
  // If a tinymce instance already exists for the given selector,
  // remove it to avoid re-initialization issues
  const id = selector.substring(1);
  if (tinymce.get(id)) tinymce.get(id)?.remove();

  tinymce.init({ ...configuration, selector });
};

const enableEditors = async () => {
  const widgets: Widgets = {
    // article fields (also newspaper issue, page and advertorial text)
    text: {
      id: '#formfield-form-widgets-text textarea',
      config: config.article,
    },
    background: {
      id: '#formfield-form-widgets-background textarea', // infobox default
      config: config.baseWithSourceAndImage,
    },
    legalnote: {
      id: '#formfield-form-widgets-legalnote textarea',
      config: config.baseWithSource,
    },
    // behaviors (image and article content types)
    author: {
      id: '#formfield-form-widgets-IAuthor-author_bio textarea', // infobox author
      config: {
        ...config.baseWithSourceAndImage,
        height: 250,
      },
    },
    imageCredit: {
      id: '#formfield-form-widgets-IImageAttachment-image_credit textarea',
      config: {
        ...config.article,
        height: 200,
        plugins: `link code`,
        toolbar: 'link unlink removeformat code',
      },
    },

    // advertorial fields
    info: {
      id: '#formfield-form-widgets-info textarea',
      config: config.baseWithSource,
    },
    wiki: {
      id: '#formfield-form-widgets-wiki_text textarea',
      config: config.article,
    },

    // comment fields
    comment: {
      id: '#form-widgets-comment-text',
      config: config.comments,
    },

    // sticky banner fields
    marketing: {
      id: '#fieldset-sticky_banner_anon textarea, #fieldset-sticky_banner_subscriber textarea, #fieldset-sticky_banner_secret_link textarea, .portaltype-freitag-article-abobreaker textarea ',
      config: config.marketing,
    },

    // generic p.a.contenttypes behavior (eg.: abo advertisement content type)
    richtext: {
      id: '#formfield-form-widgets-IRichText-text textarea',
      config: config.base,
    },

    // banner content type
    banner: {
      id: '.richtext-field',
      config: config.banner,
    },
  };

  // Check if the body has the 'userrole-manager' class
  const isManager = document.body.classList.contains('userrole-manager');

  // show the code button in the toolbar for the marketing tinyMCE editor
  if (isManager && widgets.marketing) {
    const config = widgets.marketing.config as ExtendedConfig;
    const existingToolbar = config?.toolbar;
    widgets.marketing.config = {
      ...widgets.marketing.config,
      toolbar: `${existingToolbar} | code`,
    };
  }

  Object.values(widgets).forEach(widget => {
    prepareEditor(widget.id, widget.config);
  });
};

export const customTinymce = () => {
  tinymce.addI18n('de', tinymceDE);

  enableEditors();
};
